import type { ReactNode } from "react"
import { useState } from "react"

import { cx } from "../../lib/cx"
import { useTheme } from "../../theme/use-theme"
import {
  Badge,
  Button,
  Card,
  CardHead,
  Checkbox,
  Chip,
  Chips,
  Counter,
  EmptyState,
  IconButton,
  MaskedField,
  SelectField,
  Skeleton,
  SkeletonBlock,
  SkeletonCircle,
  SkeletonText,
  StatusStripe,
  Switch,
  TextField,
  Tooltip,
} from ".."
import type { UiStrings } from "./strings"
import { DE, RU } from "./strings"
import styles from "./ui-showcase.module.css"

/**
 * Витрина библиотеки элементов, адрес /ui.
 *
 * ЗАЧЕМ ОНА НУЖНА. По ней принимают работу: на одной странице видно все состояния сразу,
 * и видно их в обеих темах, в двух языках и в колонке 360. Это дешевле и честнее, чем
 * искать «кнопку в состоянии сохранения» по живым экранам — в живом экране она бывает
 * полсекунды.
 *
 * ТРИ ПЕРЕКЛЮЧАТЕЛЯ СВЕРХУ — ЭТО И ЕСТЬ ПРОВЕРКИ.
 * 1. Тема: тёмная обязательна для оболочки и экранов мастера.
 * 2. «Текст +40 %»: немецкие строки из макета. Ни один элемент не имеет права
 *    обрезаться или вылезти за край, когда подпись становится в полтора раза длиннее.
 * 3. «Колонка 360»: та же витрина в ширину телефона мастера.
 *
 * Витрина ОТКРЫТА БЕЗ ВХОДА в салон: её смотрят на стенде, где ни у кого нет учётной
 * записи салона, и она не показывает ни одной настоящей записи — только собственные
 * демонстрационные строки из макета.
 */

const NOOP = (): void => {
  // Витрина ничего не сохраняет: у кнопок здесь нет последствий, и это намеренно.
}

export function UiShowcase(): ReactNode {
  const theme = useTheme()
  const [long, setLong] = useState(false)
  const [narrow, setNarrow] = useState(false)
  const t: UiStrings = long ? DE : RU

  // Живые переключатели: чип обязан быть настоящим переключателем, а не картинкой,
  // иначе на приёмке не видно ни фокуса, ни aria-pressed.
  const [filter, setFilter] = useState<"need" | "all" | "type">("need")
  const [consent, setConsent] = useState(true)
  const [ads, setAds] = useState(false)
  const [revealed, setRevealed] = useState(false)

  return (
    <div className={styles.page}>
      <header className={styles.bar}>
        <h1 className={styles.barTitle}>Библиотека базовых элементов · этап 0</h1>

        <span className={styles.barSpacer} />

        <div className={styles.barGroup}>
          <span className={styles.barLabel}>Тема:</span>
          <Chips label="Тема витрины">
            <Chip active={theme.mode === "light"} onClick={() => theme.setMode("light")}>
              Светлая
            </Chip>
            <Chip active={theme.mode === "dark"} onClick={() => theme.setMode("dark")}>
              Тёмная
            </Chip>
            <Chip active={theme.mode === "system"} onClick={() => theme.setMode("system")}>
              Как в системе
            </Chip>
          </Chips>
        </div>

        <div className={styles.barGroup}>
          {/* Подпись дословно из макета: этот переключатель там и стоит. */}
          <Chip active={long} onClick={() => setLong((value) => !value)}>
            Текст +40 % (проверка перевода)
          </Chip>
          <Chip active={narrow} onClick={() => setNarrow((value) => !value)}>
            Колонка 360
          </Chip>
        </div>
      </header>

      <p className={styles.intro}>
        На основе системы «Изнутри»: те же кнопки, поля, значки, чипы. Добавлено то, чего
        в системе не было и что требует ТЗ: фокус с клавиатуры, маскированное поле
        с действием «Показать», область нажатия 48 px на сенсорном экране, пустое состояние
        с одним действием, каркас загрузки, полоса состояния. Цвета в элементах нет ни
        одного — только токены, поэтому тёмная тема получается сама.
      </p>

      <div className={cx(styles.grid, narrow ? styles.narrow : undefined)}>
        <Card className={styles.stack}>
          <CardHead title="Кнопки · все состояния" />
          <div className={styles.row}>
            <Button variant="primary">{t.save}</Button>
            <Button variant="secondary">{t.cancel}</Button>
            <Button variant="danger">{t.cancelVisit}</Button>
            <Button variant="ghost">{t.later}</Button>
          </div>
          <div className={styles.row}>
            <Button variant="primary" disabled>
              {t.disabledDemo}
            </Button>
            {/* Подпись меняется, размер и место — нет: кнопка не должна уезжать
                из-под пальца, пока идёт запрос. */}
            <Button variant="primary" loading loadingLabel={t.saving}>
              {t.save}
            </Button>
          </div>
          <div className={styles.row}>
            {/*
             * Выключено ПРАВОМ. Кнопка видна, доходит до неё Tab, пояснение читается
             * и мышью, и с экрана — это требование макета, а не украшение.
             */}
            <Button variant="secondary" deniedReason={t.editPastDenied}>
              {t.editPast}
            </Button>
          </div>
          <p className={styles.note}>↑ выключено правом — с пояснением, не спрятано</p>
          <div className={styles.row}>
            <Button variant="primary" size="sm">
              {t.book}
            </Button>
            <Button variant="secondary" size="sm">
              {t.reschedule}
            </Button>
            <Button variant="danger-ghost" size="sm">
              {t.noShow}
            </Button>
          </div>
          <Button variant="primary" size="touch" block>
            {t.book}
          </Button>
          <p className={styles.note}>
            ↑ размер «под палец»: 48 px по высоте — главная кнопка на телефоне мастера.
            У остальных кнопок область нажатия дорастает до 48 px на сенсорном экране,
            а видимый размер не меняется.
          </p>
        </Card>

        <Card className={styles.stack}>
          <CardHead title="Кнопки-значки · счётчики" />
          <div className={styles.row}>
            <IconButton label="Уведомления" framed icon={<BellGlyph />} />
            <IconButton
              // Число из счётчика повторено в имени: aria-label заменяет собой
              // содержимое кнопки, и иначе «3» было бы видно, но не слышно.
              label={`Уведомления, 3 ${t.notifications}`}
              framed
              icon={<BellGlyph />}
              badge={<Counter value={3} label={t.notifications} tone="attention" corner />}
            />
            <IconButton
              label={`Уведомления, 3 ${t.notifications}`}
              framed
              size="lg"
              icon={<BellGlyph />}
              badge={<Counter value={3} label={t.notifications} tone="attention" corner />}
            />
            <IconButton label="Настройки раздела" icon={<GearGlyph />} />
            <IconButton label="Удалить заметку" icon={<CrossGlyph />} disabled />
          </div>
          <p className={styles.note}>
            У значка обязательно есть имя словами: без него кнопка недоступна ни программе
            чтения с экрана, ни человеку, впервые увидевшему колокольчик. Размер 40×40 —
            тот же колокольчик на телефоне мастера.
          </p>
          <div className={styles.row}>
            <Counter value={3} label={t.notifications} tone="attention" />
            <Counter value={14} label={t.visits} tone="quiet" />
            <Counter value={48} label={t.visits} tone="quiet" />
          </div>
        </Card>

        <Card className={styles.stack}>
          <CardHead title="Чипы-переключатели" />
          <Chips label="Фильтр списка">
            <Chip
              active={filter === "need"}
              count={3}
              countLabel="требуют действия"
              onClick={() => setFilter("need")}
            >
              {t.needAction}
            </Chip>
            <Chip active={filter === "all"} count={48} countLabel="всего" onClick={() => setFilter("all")}>
              {t.all}
            </Chip>
            <Chip active={filter === "type"} onClick={() => setFilter("type")}>
              {t.byType}
            </Chip>
            <Chip disabled>{t.byType}</Chip>
          </Chips>
          <p className={styles.note}>
            Выбранный чип отличается не только заливкой: у него aria-pressed, поэтому выбор
            слышно в программе чтения с экрана и видно в градациях серого.
          </p>
          <div className={styles.grayscale}>
            <Chips label="Тот же ряд в градациях серого">
              <Chip active>{t.needAction}</Chip>
              <Chip>{t.all}</Chip>
            </Chips>
          </div>
        </Card>

        <Card className={styles.stack}>
          <CardHead title="Поля · включая маскированное" />
          <TextField
            label={t.phoneLabel}
            required
            hint={t.phoneHint}
            defaultValue="+7 916 123-45-67"
            inputMode="tel"
          />
          <TextField label={t.serviceLabel} defaultValue={t.serviceValue} />
          <TextField label={t.phoneErrorLabel} defaultValue="+7 916 12" error={t.phoneError} />
          <MaskedField
            label={t.phoneMaskedLabel}
            masked="+7 916 ••• •• 67"
            revealed={revealed ? "+7 916 123-45-67" : undefined}
            revealLabel={t.reveal}
            spoken={t.phoneMaskedSpoken}
            hint={t.revealNote}
            onReveal={() => setRevealed(true)}
          />
          <SelectField
            label={t.reasonLabel}
            options={[
              { value: "client", label: t.reasonClient },
              { value: "salon", label: t.reasonSalon },
            ]}
          />
          <Checkbox checked={consent} onChange={setConsent}>
            {t.consentSpoken}
          </Checkbox>
          <Switch checked={ads} onChange={setAds}>
            {t.consentAds}
          </Switch>
          <TextField label={t.serviceLabel} defaultValue={t.serviceValue} disabled />
        </Card>

        <Card className={styles.stack}>
          <CardHead title="Значки состояния" />
          <div className={styles.row}>
            <Badge tone="ok">{t.badgeConfirmed}</Badge>
            <Badge tone="warn">{t.badgePending}</Badge>
            <Badge tone="info">{t.badgeInProgress}</Badge>
            <Badge tone="gray">{t.badgeCompleted}</Badge>
            <Badge tone="error">{t.badgeNoShow}</Badge>
            <Badge tone="outline">{t.badgeFirstVisit}</Badge>
            <Badge tone="brand">{t.badgeRestricted}</Badge>
          </div>
          <p className={styles.caption}>Тот же ряд в градациях серого</p>
          <div className={cx(styles.row, styles.grayscale)}>
            <Badge tone="ok">{t.badgeConfirmed}</Badge>
            <Badge tone="warn">{t.badgePending}</Badge>
            <Badge tone="info">{t.badgeInProgress}</Badge>
            <Badge tone="gray">{t.badgeCompleted}</Badge>
            <Badge tone="error">{t.badgeNoShow}</Badge>
            <Badge tone="outline">{t.badgeFirstVisit}</Badge>
            <Badge tone="brand">{t.badgeRestricted}</Badge>
          </div>
          <p className={styles.note}>
            Значок читается подписью, а не цветом: обесцвеченный ряд обязан остаться
            понятным. Цвет только ускоряет чтение тому, кто уже знает, что где.
          </p>
        </Card>

        <Card className={styles.stack}>
          <CardHead title="Подсказка при наведении" />
          <div className={styles.row}>
            <Tooltip text={t.tooltipText}>
              <Badge tone="warn">{t.badgePending}</Badge>
            </Tooltip>
          </div>
          <p className={styles.note}>
            Появляется через 400 мс от мыши и сразу от клавиатуры, закрывается Escape.
            Всё, что в ней сказано, обязано быть доступно и другим путём: мастер на телефоне
            её не откроет.
          </p>
        </Card>

        <Card className={styles.stack}>
          <CardHead title="Каркас загрузки" />
          <div className={styles.skeletonRow}>
            <Skeleton width={120} height={14} />
            <Skeleton width={180} height={14} />
            <span className={styles.checking}>{t.checking}</span>
          </div>
          <SkeletonBlock label={t.loadingClients}>
            <div className={styles.skeletonRow}>
              <SkeletonCircle size={26} />
              <SkeletonText widths={[140, 110]} />
            </div>
            <div className={styles.skeletonRow}>
              <SkeletonCircle size={26} />
              <SkeletonText widths={[110, 90]} />
            </div>
            <Skeleton width="100%" height={52} radius={8} />
          </SkeletonBlock>
          <p className={styles.note}>
            Тот же каркас служит состоянием «проверяем доступ»: пока права неизвестны,
            экран обязан выглядеть как обычная загрузка, иначе человеку с полными правами
            на долю секунды покажут отказ.
          </p>
        </Card>

        <Card className={styles.stack}>
          <CardHead title="Пустое состояние" />
          <EmptyState
            title={t.emptyTitle}
            text={t.emptyText}
            action={{ label: t.emptyAction, onClick: NOOP }}
          />
          <EmptyState title={t.emptyQuietTitle} text={t.emptyQuietText} />
          <p className={styles.note}>
            ↑ то же состояние у администратора: действия в пределах его прав нет —
            и кнопки нет вовсе, а не выключенная.
          </p>
        </Card>

        <Card className={cx(styles.stack, styles.wide)} padded={false}>
          <CardHead title="Полосы состояния · порядок сверху вниз фиксирован" />
          <div className={styles.stripes}>
            <StatusStripe
              tone="offline"
              title={t.offlineTitle}
              text={t.offlineText}
              action={{ label: t.offlineAction, onClick: NOOP }}
            />
            <StatusStripe
              tone="impersonate"
              title={t.impersonateTitle}
              text={t.impersonateText}
              action={{ label: t.impersonateAction, onClick: NOOP }}
            />
            <StatusStripe
              tone="billing"
              title={t.billingTitle}
              text={t.billingText}
              action={{ label: t.billingAction, onClick: NOOP }}
            />
          </div>
          <div className={styles.phoneBody}>
            <p className={styles.note}>
              Полоса — это состояние экрана, а не всплывающее сообщение: она не исчезает
              сама и стоит своей строкой. Ниже те же полосы в ширину телефона мастера —
              там у них нет фиксированной высоты, и текст переносится: обрезанное
              многоточием предупреждение о том, что изменения не сохраняются, — это
              предупреждение, которого не прочли.
            </p>
            <div className={styles.phone}>
              <div className={styles.stripes}>
                <StatusStripe
                  tone="offline"
                  title={t.offlineTitle}
                  text={t.offlineText}
                  action={{ label: t.offlineAction, onClick: NOOP }}
                />
                <StatusStripe
                  tone="billing"
                  title={t.billingTitle}
                  text={t.billingText}
                  action={{ label: t.billingAction, onClick: NOOP }}
                />
              </div>
              <div className={styles.phoneBody}>
                <Button variant="primary" size="touch" block>
                  {t.book}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}

/* Значки витрины. Собственного набора значков библиотека не заводит: она принимает
   готовый узел, а набор — отдельное решение этапа 1. */

function BellGlyph(): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="16"
      height="16"
    >
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </svg>
  )
}

function GearGlyph(): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="16"
      height="16"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3m0 14v3M4.2 4.2l2.1 2.1m11.4 11.4 2.1 2.1M2 12h3m14 0h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1" />
    </svg>
  )
}

function CrossGlyph(): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="16"
      height="16"
    >
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  )
}
